import { launchShows } from './launchShows.ts'

export type RoomSelection = {
  id: string
  title: string
  season: number | null
  roomSlug: string
  nightLabel: string
  accent: 'plum' | 'coral'
}

export type LiveRoomSummary = RoomSelection & {
  description: string
  state: 'pre' | 'live' | 'post' | 'locked'
  publicCanPost: boolean
  adminCanPost: boolean
  startsAt: string | null
  opensAt: string | null
  locksAt: string | null
  messageCount: number
  isDemo: boolean
}

const validDate = (value: unknown): string | null => typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null

export function roomSummariesFromPayload(payload: unknown): LiveRoomSummary[] {
  if (!payload || typeof payload !== 'object' || !Array.isArray((payload as Record<string, unknown>).rooms)) {
    throw new Error('The room list could not be verified.')
  }
  return (payload as {rooms: unknown[]}).rooms.filter((value) => {
    return !(value && typeof value === 'object' && (value as Record<string, unknown>).status === 'cancelled')
  }).map((value): LiveRoomSummary => {
    if (!value || typeof value !== 'object') throw new Error('The room list could not be verified.')
    const room = value as Record<string, unknown>
    if (typeof room.id !== 'string' || !room.id || typeof room.slug !== 'string'
      || room.slug.length > 160 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(room.slug)
      || typeof room.title !== 'string' || !room.title.trim()
      || !['pre','live','post','locked'].includes(String(room.state))) {
      throw new Error('The room list could not be verified.')
    }
    const show = launchShows.find((item) => item.roomSlug === room.slug)
    const count = typeof room.message_count === 'number' && Number.isSafeInteger(room.message_count) && room.message_count >= 0 ? room.message_count : 0
    return {
      id: room.id,
      roomSlug: room.slug,
      title: show?.title ?? room.title.trim(),
      season: typeof room.season === 'number' && Number.isSafeInteger(room.season) && room.season > 0 ? room.season : null,
      nightLabel: show?.nightLabel ?? 'Fan',
      accent: show?.accent ?? 'plum',
      description: typeof room.description === 'string' ? room.description : '',
      state: room.state as LiveRoomSummary['state'],
      publicCanPost: room.public_can_post === true,
      adminCanPost: room.admin_can_post === true,
      startsAt: validDate(room.scheduled_starts_at),
      opensAt: validDate(room.posting_opens_at),
      locksAt: validDate(room.posting_locks_at),
      messageCount: count,
      isDemo: room.is_demo === true,
    }
  })
}

export function mergeRoomSummaries(current: LiveRoomSummary[], incoming: LiveRoomSummary[]) {
  return [...new Map([...current,...incoming].map((room) => [room.roomSlug,room])).values()]
}

export function roomSelectionForRoom(room: LiveRoomSummary): RoomSelection {
  return {id:room.id,title:room.title,season:room.season,roomSlug:room.roomSlug,nightLabel:room.nightLabel,accent:room.accent}
}

export function roomStatusLabel(room: LiveRoomSummary) {
  if (room.publicCanPost) return room.state === 'live' ? 'On air · Chat open' : room.state === 'post' ? 'Post-show chat' : 'Chat open'
  return room.state === 'locked' || !room.opensAt ? 'Read-only' : 'Member chat opens before showtime'
}

export function roomPageOffset(payload: unknown): number | null {
  const page = (payload as {pagination?: Record<string, unknown>} | null)?.pagination
  if (!page || typeof page.has_more !== 'boolean') throw new Error('The room-list page could not be verified.')
  if (!page.has_more) return null
  if (typeof page.next_offset !== 'number' || !Number.isSafeInteger(page.next_offset) || page.next_offset < 1) {
    throw new Error('The next room-list page could not be verified.')
  }
  return page.next_offset
}
